/**
 * SB6-II-01 — pure helpers for the inline cells' optimistic
 * update / rollback over the cached list pages.
 *
 * Extracted from the cell components so the commit/rollback logic is
 * unit-testable without a live QueryClient render. A "snapshot" is the
 * `[queryKey, data]` tuple returned by `queryClient.getQueriesData`; we
 * apply the optimistic patch to each snapshot's `items`, and the
 * rollback simply restores the captured snapshots.
 */
import type { IiListItem, IiListResponse } from "./types.js";

export type ListSnapshot = readonly [
  key: readonly unknown[],
  data: IiListResponse | undefined,
];

/**
 * Apply `patch` to the matching row across every snapshot's `items`,
 * returning a NEW `[key, data]` list (never mutating the input). Rows
 * that don't match are returned untouched; `undefined` snapshots pass
 * through unchanged.
 */
export function applyOptimisticRowPatch(
  snapshots: readonly ListSnapshot[],
  inspectionInstructionId: string,
  patch: Partial<IiListItem>,
): ListSnapshot[] {
  return snapshots.map(([key, data]): ListSnapshot => {
    if (!data) return [key, data];
    return [
      key,
      {
        ...data,
        items: data.items.map((r) =>
          r.inspectionInstructionId === inspectionInstructionId
            ? { ...r, ...patch }
            : r,
        ),
      },
    ];
  });
}
